import { useState, useEffect, useRef } from 'react'
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet'
import 'leaflet/dist/leaflet.css'
import 'leaflet-routing-machine/dist/leaflet-routing-machine.css'
import 'leaflet-routing-machine'
import '@/styles/map.css'
import { dealers } from './locations'
import { Dealer } from '@/types'
import L from 'leaflet'

declare module 'leaflet' {
  namespace Routing {
    function control(options: any): any;
  }
}

const dealerIcon = L.icon({
  iconUrl: '/location/dealerPin.svg',
  iconSize: [38, 38],
  iconAnchor: [19, 38],
  popupAnchor: [0, -38],
})

const dealerIconActive = L.icon({
  iconUrl: '/location/dealerPin.svg',
  iconSize: [50, 50],
  iconAnchor: [25, 50],
  popupAnchor: [0, -50],
})

const userIcon = L.icon({
  iconUrl: '/location/mePin.svg',
  iconSize: [38, 38],
  iconAnchor: [19, 38],
  popupAnchor: [0, -38],
})

function CenterMap({ position }: { position: number[] }) {
  const map = useMap()
  useEffect(() => {
    if (position) {
      map.setView(position as L.LatLngExpression, 14, { animate: true, duration: 0.6 })
    }
  }, [position, map])
  return null
}

function RoutingMachine({ userPosition, destination }: { userPosition: number[], destination: number[] }) {
  const map = useMap()
  const routingControlRef = useRef(null)

  useEffect(() => {
    if (userPosition && destination) {
      if (routingControlRef.current) {
        map.removeControl(routingControlRef.current)
      }

      routingControlRef.current = L.Routing.control({
        waypoints: [
          L.latLng(userPosition[0], userPosition[1]),
          L.latLng(destination[0], destination[1])
        ],
        routeWhileDragging: true,
        lineOptions: {
          styles: [{ color: '#E10F18', weight: 4, opacity: 0.8 }]
        },
        show: false,
        addWaypoints: false,
        draggableWaypoints: false,
        fitSelectedRoutes: true,
        showAlternatives: false
      }).addTo(map)
    }

    return () => {
      if (routingControlRef.current) {
        map.removeControl(routingControlRef.current)
      }
    }
  }, [map, userPosition, destination])

  return null
}

interface DealersMapProps {
  selectedDealer: Dealer | null
  setSelectedDealer: (dealer: Dealer | null) => void
  hoveredDealer: Dealer | null
}

export function DealersMap({ selectedDealer, setSelectedDealer, hoveredDealer }: DealersMapProps) {
  const [userPosition, setUserPosition] = useState<number[] | null>(null)
  const [destination, setDestination] = useState<number[] | null>(null)

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserPosition([position.coords.latitude, position.coords.longitude])
        },
        (error) => {
          console.error("Error getting user's location:", error)
        }
      )
    }
  }, [])

  useEffect(() => {
    if (selectedDealer) {
      setDestination([selectedDealer.lat, selectedDealer.lng])
    }
  }, [selectedDealer])

  const activeDealer = hoveredDealer || selectedDealer

  return (
    <div className="relative h-full w-full">
      <MapContainer
        center={[18.4566342, -69.296238]}
        zoom={9}
        style={{ height: '100%', width: '100%' }}
        className="leaflet-container"
        zoomControl={false}
      >
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/">CARTO</a>'
        />
        {dealers.map((dealer) => {
          const isActive = activeDealer?.id === dealer.id
          return (
            <Marker
              key={dealer.id}
              position={[dealer.lat, dealer.lng]}
              icon={isActive ? dealerIconActive : dealerIcon}
              eventHandlers={{
                click: () => setSelectedDealer(dealer),
              }}
            >
              <Popup>
                <div className="min-w-[180px]">
                  <p className="font-bold text-[#1F2240] text-sm">{dealer.name}</p>
                  <p className="text-xs text-gray-500 mt-1">{dealer.address}</p>
                  {dealer.phone && (
                    <p className="text-xs text-gray-600 mt-1 font-medium">{dealer.phone}</p>
                  )}
                </div>
              </Popup>
            </Marker>
          )
        })}
        {userPosition && (
          <Marker position={userPosition as L.LatLngExpression} icon={userIcon}>
            <Popup>Tu ubicación</Popup>
          </Marker>
        )}
        {selectedDealer && <CenterMap position={[selectedDealer.lat, selectedDealer.lng]} />}
        {userPosition && destination && (
          <RoutingMachine userPosition={userPosition} destination={destination} />
        )}
      </MapContainer>
    </div>
  )
}
